import api from './api';
import { Order, CreateOrderRequest } from '../types/order.types';
import { RxBumpInput } from '../types/billing.types';
import type { AgingBucketStats } from '../types/agingBucket.types';
import { formatDeliveryAddressSnapshot, NO_DELIVERY_ADDRESS_LABEL } from '../utils/addressFormat';
import type { PmsGateResult } from '../types/pmsGate';
import type { PmsDatabaseType } from '../types/pms';

/** PRX-165 WP-26 — the Make shipment answer: one row per order, never all or nothing. */
export interface MakeShipmentResponse {
  success: boolean;
  message: string;
  courierId: string;
  courierName: string;
  /** 'api': submitted through the courier's client. 'manual': nothing was sent; book it with the courier. */
  mode: 'api' | 'manual';
  results: Array<{
    orderId: string;
    orderNumber?: string;
    success: boolean;
    skipped?: boolean;
    bagNumber?: string;
    externalId?: string;
    /** PRX-21 WP-31: PHOX booked this order with the courier itself (Meds4You, Canada Post). */
    bookedWithCourier?: boolean;
    /** PRX-21 WP-31: the courier's tracking number, when it gave one. */
    trackingNumber?: string;
    error?: string;
    /** PRX-21 WP-31: the courier may or may not have booked it. Nothing was bagged; check the courier first. */
    outcomeUnknown?: boolean;
    /** On a success: the order is in its bag, but something after the bag was not saved. */
    warning?: string;
  }>;
  summary: { successCount: number; skippedCount: number; failureCount: number };
}

export interface AlertStatusItem {
  id: string;
  orderNumber?: string;
  patientName: string;
  patientId?: string;
  rxNumber: string;
  drugName: string;
  deliveryDate: string;
  deliveryType?: string;
  workflowStage?: string;
  status?: string;
  alertReason: 'COURIER_NO_DELIVERY_SIGNAL' | 'INHOUSE_NO_COMPLETE_WEBHOOK' | 'MISSED_UNRESOLVED' | 'LTOM_AWAITING_MISSISSAUGA' | 'AGING';
  daysOverdue: number;
  trackingNumber?: string;
  carrier?: string;
  shipstationTrackingStatus?: string;
  followUpUpdateFailed: boolean;
  createdAt: string;
  updatedAt: string;
}

const API_PREFIX = '/api';

/**
 * GP-1380 — the six criteria a Swap RX must satisfy. Mirrors
 * `backend-v2/src/orders/utils/rx-swap-equivalence.util.ts`, which computes
 * these; the client only renders them. Change one and change the other.
 */
export type RxSwapCriterion =
  | 'patient'
  | 'din'
  | 'quantity'
  | 'originalRx'
  | 'daysSupply'
  | 'sig';

export interface RxSwapCheck {
  criterion: RxSwapCriterion;
  label: string;
  oldValue: string;
  newValue: string;
  passed: boolean;
  /** Kroll had no value on one side — reported as a failure, never a pass. */
  unverified: boolean;
}

export interface RxSwapEquivalence {
  oldRxNumber: string;
  newRxNumber: string;
  checks: RxSwapCheck[];
  allPassed: boolean;
  failedCriteria: RxSwapCriterion[];
  failedLabels: string;
}

export interface RxCancellationStatusItem {
  rxNumber: string;
  /** Kroll numeric code, or the Fillware `Rx.Status` letter (null = live fill or not found). */
  status: number | string | null;
  statusLabel: string;
  /** The PMS returned this Rx. Use this, not `status`: a live Fillware fill has status null. */
  found?: boolean;
  isCancelled: boolean;
  /** The pharmacy's system. Per row so a filtered `rxStatuses` list still knows it. */
  databaseType?: PmsDatabaseType;
  /** GP-1161: another non-cancelled order of the same patient that carries this Rx. */
  inUseByOrderNumber?: string | null;
  inUseByOrderStage?: string | null;
  /** Cancelled in Kroll OR validly owned by another live order. */
  isResolvedForCancellation?: boolean;
}

export interface RxCancellationStatus {
  orderId: string;
  orderNumber: string;
  databaseType?: PmsDatabaseType;
  rxStatuses: RxCancellationStatusItem[];
  /** True only when every Rx on the order is cancelled (Kroll status 2). */
  allCancelled: boolean;
  /** False if any Rx could not be resolved in Kroll (gate must stay closed). */
  allResolved: boolean;
  /**
   * GP-1161: true when every Rx is either cancelled in Kroll or in use by
   * another non-cancelled order — the cancel can proceed even though the
   * shared Rxs stay Active (they belong to the other order).
   */
  canFinalize?: boolean;
  message?: string;
}

// Universal Search Types
export interface UniversalSearchRequest {
  query: string;
  type?: 'all' | 'orderId' | 'rxNumber' | 'patient' | 'phone';
  pharmacyId?: string;
}

export interface UniversalOrderData {
  orderId: string;
  orderNumber: string;
  orderType: 'regular' | 'refill' | 'subscription' | 'compound' | 'narcotics';
  status: string;
  stage: string;
  createdAt: string;
  updatedAt: string;

  patient: {
    id: string;
    name: string;
    phone: string;
    address: string;
    dateOfBirth?: string;
    insuranceStatus?: string;
  };

  medications: Array<{
    name: string;
    strength: string;
    quantity: number;
    daysSupply: number;
    rxNumber: string;
    isFOC: boolean;
    isNarcotic: boolean;
    status: string;
  }>;

  workflow: {
    currentStep: string;
    completedSteps: string[];
    pendingSteps: string[];
    blockers: string[];
    assignedTo?: string;
    priority: 'critical' | 'high' | 'on_time' | 'low';
  };

  delivery?: {
    method: 'pickup' | 'delivery' | 'mail';
    scheduledDate?: string;
    actualDate?: string;
    address?: string;
    trackingNumber?: string;
    carrier?: string;
    status?: string;
    signature?: boolean;
  };

  billing?: {
    total: number;
    copay: number;
    insuranceCovered: number;
    status: 'pending' | 'approved' | 'rejected' | 'paid';
    claimNumber?: string;
    rejectionReason?: string;
  };

  integrations: {
    kroll?: any;
    phox?: any;
    descartes?: any;
    nexi?: any;
  };

  timeline: Array<{
    timestamp: string;
    event: string;
    description: string;
    user?: string;
    type: 'success' | 'warning' | 'error' | 'info';
    metadata?: any;
  }>;

  relatedOrders?: Array<{
    orderId: string;
    type: string;
    relationship: string;
  }>;
}

export interface WorkflowStep {
  stepNumber: number;
  name: string;
  status: 'active' | 'inactive';
  queueCount: number;
  completedToday: number;
  validationStatus?: string;
  avgProcessingTime?: string;
  pendingPharmacistReview?: number;
  krollAdjudicationPending?: number;
}

export interface TroubleshootingQueue {
  count: number;
  critical: number;
  averageAge: string;
}

export interface WorkflowOverview {
  workflowSteps: WorkflowStep[];
  troubleshootingQueue: TroubleshootingQueue;
}

export interface Patient {
  name: string;
  patientId: string;
  age: number;
  phone: string;
}

export interface Drug {
  name: string;
  isFOC: boolean;
  phoxValidated: boolean;
  daysSupply: number;
}

export interface WorkflowStatus {
  currentStep: number;
  completedSteps: string[];
  status: 'in_progress' | 'completed' | 'error' | 'on_track';
  urgency: 'critical' | 'urgent' | 'warning' | 'attention' | 'normal';
  autoCalculatedFollowUp?: string;
}

export interface IntegrationStatus {
  krollStatus: string;
  phoxStatus: string;
  descartesRoute: string;
}

export interface OrderItem {
  orderId: string;
  patient: Patient;
  drug: Drug;
  followUpDate: string;
  deliveryType: string;
  workflow: WorkflowStatus;
  integrations: IntegrationStatus;
  actions: string[];
  issue?: string;
  daysInQueue?: number;
  assignedTo?: string;
  shipDate?: string;
  carrier?: string;
}

export interface QueueFilters {
  applied: string[];
  available: {
    coordinators: string[];
    drugTypes: string[];
    urgencyLevels: string[];
  };
}

export interface QueueOrdersResponse {
  queueType: string;
  orders: OrderItem[];
  totalCount: number;
  filters: QueueFilters;
}

export interface OrderWorkflowDetail {
  orderId: string;
  workflowStatus: {
    currentStep: number;
    currentStepName: string;
    progress: number;
    steps: Array<{
      step: number;
      name: string;
      status: 'completed' | 'in_progress' | 'pending';
      completedAt?: string;
      startedAt?: string;
      completedBy?: string;
      assignedTo?: string;
      validations?: Record<string, string>;
      details?: Record<string, any>;
      pendingItems?: string[];
    }>;
    blockers: string[];
    estimatedCompletion: string;
  };
}

export interface IntegrationHealthStatus {
  integrations: {
    phox: {
      status: string;
      health: string;
      lastHeartbeat: string;
      activeConnections: number;
      queueDepth: number;
    };
    kroll: {
      status: string;
      adjudicationEnabled: boolean;
      pendingAdjudications: number;
      averageResponseTime: string;
      lastSync: string;
    };
    descartes: {
      status: string;
      availableRoutes: number;
      nextRouteUpdate: string;
      deliveryCapacity: {
        today: string;
        tomorrow: string;
      };
    };
    realTimeValidation: {
      status: string;
      validationsPerMinute: number;
      errorRate: string;
      averageValidationTime: string;
    };
  };
  overall: string;
  lastUpdated: string;
}

export interface WorkflowMetrics {
  date: string;
  metrics: {
    totalActiveOrders: number;
    phoxValidated: number;
    completedToday: number;
    averageCompletionTime: string;
    bottlenecks: Array<{
      step: string;
      averageWaitTime: string;
      queueSize: number;
    }>;
    focMetrics: {
      focOrders: number;
      focPercentage: number;
      focPrograms: string[];
    };
    deliveryMetrics: {
      scheduledToday: number;
      onTimeDelivery: string;
      routeUtilization: string;
    };
  };
}

// The dedicated `/orders/universal-search` endpoint was never implemented on the
// backend — it 404s, so order lookup by number silently failed everywhere it was
// used (the "Add Counseling Task" dialog and the Universal Order Search page). The
// order-list endpoint (`GET /orders/:pharmacyId?search=`) already resolves an order
// by order number, patient name, or Rx number, so we map its first hit into the
// UniversalOrderData shape the callers consume.
function mapOrderRowToUniversal(row: any): UniversalOrderData {
  // The order-list row (GET /orders/:pharmacyId) is the raw Order entity — there is no
  // `row.deliveryAddress` string and no nested `row.patient` object (patient fields are
  // flattened: patientId/patientName/patientPhone). The real address source is the frozen
  // `deliveryAddressSnapshot` JSONB column (clinic variant takes priority, same precedence
  // as UnifiedRxNavigator's formatter). No silent '' fallback — an unresolvable address
  // renders an explicit marker per the repo's no-silent-fallback rule.
  const formattedAddress = formatDeliveryAddressSnapshot(
    row.deliveryAddressSnapshot,
    row.deliveryAddressSnapshotClinic,
  );

  return {
    orderId: row.id ?? '',
    orderNumber: row.orderNumber ?? '',
    orderType: row.orderSource === 'subscription' ? 'subscription' : 'regular',
    status: row.status ?? row.orderStatus ?? '',
    stage: row.workflowStage ?? '',
    createdAt: row.createdAt ?? '',
    updatedAt: row.updatedAt ?? '',
    patient: {
      id: row.patientId ?? '',
      name: row.patientName ?? '',
      phone: row.patientPhone ?? '',
      address: formattedAddress || NO_DELIVERY_ADDRESS_LABEL,
      // Order rows never carry a date of birth (no `dob`/`dateOfBirth` field on the
      // entity, no patient join in this endpoint) — left undefined rather than reading
      // a field that doesn't exist.
      dateOfBirth: undefined,
    },
    medications: [],
    workflow: {
      currentStep: row.workflowStage ?? '',
      completedSteps: [],
      pendingSteps: [],
      blockers: [],
      // Order.assignedTo is a real top-level column (assigned_to) and survives the
      // list endpoint's full-entity select — this read is accurate.
      assignedTo: row.assignedTo ?? undefined,
      priority: (row.priority as UniversalOrderData['workflow']['priority']) ?? 'on_time',
    },
    delivery: row.deliveryMethod
      ? {
          method: row.deliveryMethod as NonNullable<UniversalOrderData['delivery']>['method'],
          scheduledDate: row.deliveryDate ?? undefined,
          address: formattedAddress || undefined,
        }
      : undefined,
    integrations: {},
    timeline: [],
  };
}

/**
 * Mirrors `OrderPmsGateResponse` in `backend-v2/src/orders/orders.service.ts`
 * (PRX-59). `hasBlocking` is the backend's own flag and is what a scan point
 * refuses on — it is false for every Kroll verdict, so the Kroll axis stays
 * advisory. Never re-derive it from `verdict`.
 */
export interface OrderPmsGateResponse {
  orderId: string;
  orderNumber: string;
  databaseType: PmsDatabaseType;
  gates: Array<{ rxNumber: string; medicationName: string | null; gate: PmsGateResult }>;
  hasCancelled: boolean;
  hasBlocking: boolean;
}

export const orderService = {
  // Universal Search — resolves a single order via the order-list search endpoint.
  //
  // Release 2138 review: the backend search is an ILIKE match, so `limit: 1`
  // took whatever order happened to sort first — CreateTaskDialog could link a
  // task to a DIFFERENT order on a partial or mistyped number. For order-number
  // lookups (`type: 'orderId'`) we now fetch a small page and demand an exact,
  // case-insensitive orderNumber match; no exact match resolves to null so the
  // caller shows its "Order not found" state instead of linking a guess. Other
  // search types (patient / phone / rx on the Universal Order Search page) keep
  // the fuzzy first-hit behaviour — those queries are not order numbers.
  async searchUniversal(searchRequest: UniversalSearchRequest): Promise<UniversalOrderData | null> {
    const { query, pharmacyId, type } = searchRequest;
    if (!pharmacyId || !query?.trim()) return null;
    const wanted = query.trim();
    const requireExactOrderNumber = type === 'orderId';
    try {
      const { data } = await orderService.getOrders(pharmacyId, {
        search: wanted,
        // 50, not 10: the backend sorts fuzzy matches createdAt DESC, and a base
        // order's CC-/RP-/TS-/BO- clones all CONTAIN its number — a small page
        // could push the older exact row out and report "not found" for an
        // order that exists (Codex P2, PR #2301).
        limit: requireExactOrderNumber ? 50 : 1,
      });
      const rows = data ?? [];
      const row = requireExactOrderNumber
        ? rows.find(
            (r: any) =>
              typeof r?.orderNumber === 'string' &&
              r.orderNumber.toLowerCase() === wanted.toLowerCase(),
          )
        : rows[0];
      return row ? mapOrderRowToUniversal(row) : null;
    } catch (error) {
      console.error('Universal search error:', error);
      return null;
    }
  },

  // Order CRUD operations
  async createOrder(orderData: CreateOrderRequest): Promise<Order> {
    const { pharmacyId, ...orderPayload } = orderData;
    const response = await api.post(`${API_PREFIX}/orders/${pharmacyId}`, orderPayload);
    return response.data;
  },

  async createAlreadyBilled(
    pharmacyId: string,
    dto: {
      // Base CreateOrderDto-like fields
      patientId: string;
      patientName?: string;
      orderType: string;
      orderSource: string;
      priority?: string;
      prescriberName?: string;
      prescriberPhone?: string;
      prescriberFax?: string;
      deliveryMethod?: string;
      deliveryAddress?: string;
      deliveryDate: string;
      nextFollowUpDate?: string;
      specialInstructions?: string;
      internalNotes?: string;
      consultationRequired?: boolean;
      requiresCompound?: boolean;
      requiresSpecialPackaging?: boolean;
      temperatureControlled?: boolean;
      hasNarcotics?: boolean;
      items: any[];
      patientNumber?: string;
      deliveryAddressId?: string;
      deliveryAddressSnapshot?: Record<string, any>;
      confirmationStatus?: string;
      deliveryDateOverridden?: boolean;
      urgentDelivery?: boolean;
      overrideDeliveryDate?: string;
      overrideReason?: string;
      citySchedule?: any;
      metadata?: any;
      // Already-billed specific
      billedRxNumber: string;
      sendNotification?: boolean;
      notificationPhone?: string;
      billingNotes?: string;
    },
  ): Promise<{
    success: boolean;
    order: {
      id: string;
      orderNumber: string;
      status: string;
      workflowStage: string;
      billedRxNumber: string;
    };
    notification: any;
    message: string;
  }> {
    const response = await api.post(
      `${API_PREFIX}/orders/${pharmacyId}/create-already-billed`,
      dto,
    );
    return response.data;
  },

  async getOrders(
    pharmacyId: string,
    params?: {
      page?: number;
      limit?: number;
      status?: string;
      priority?: string;
      patientId?: string;
      dateFrom?: string;
      dateTo?: string;
      search?: string;
    },
  ): Promise<{ data: any[]; total: number; page: number; totalPages: number }> {
    const queryParams = new URLSearchParams();

    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          queryParams.append(key, value.toString());
        }
      });
    }

    const url = `${API_PREFIX}/orders/${pharmacyId}?${queryParams.toString()}`;
    console.log('Fetching orders from:', url);
    const response = await api.get(url);
    console.log('Order service response:', response.data);
    return response.data;
  },

  async getOrderById(pharmacyId: string, orderId: string): Promise<any> {
    const response = await api.get(`${API_PREFIX}/orders/${pharmacyId}/${orderId}`);
    // API returns { success: true, data: {...} }
    if (response.data && response.data.data) {
      return response.data.data;
    }
    // Fallback if structure is different
    return response.data;
  },

  /**
   * Get action logs and stage history for an order
   * Returns comprehensive audit trail with entry/exit times and durations
   */
  async getOrderActionLogs(
    pharmacyId: string,
    orderId: string,
    params?: { limit?: number; offset?: number },
  ): Promise<{
    logs: Array<{
      id: string;
      actionType: string;
      actionDescription: string;
      fromStage?: string;
      toStage?: string;
      performedByUserId?: string;
      performedByUserName?: string;
      metadata?: Record<string, any>;
      createdAt: string;
    }>;
    stageHistory: Array<{
      stage: string;
      stageLabel: string;
      enteredAt?: string;
      exitedAt?: string;
      durationMs?: number;
      durationFormatted?: string;
      performedByUserId?: string;
      performedByUserName?: string;
      status: 'completed' | 'current' | 'pending' | 'skipped';
    }>;
    totalCount: number;
    summary: {
      totalActions: number;
      firstAction?: string;
      lastAction?: string;
      currentStage?: string;
      timeInCurrentStage?: number;
    };
  }> {
    const queryParams = new URLSearchParams();
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.offset) queryParams.append('offset', params.offset.toString());

    const response = await api.get(
      `${API_PREFIX}/orders/${pharmacyId}/${orderId}/action-logs?${queryParams.toString()}`,
    );
    // API returns { success: true, data: {...} }
    if (response.data && response.data.data) {
      return response.data.data;
    }
    return response.data;
  },

  /**
   * GP-1390 — other LIVE orders of this patient that carry a prescription which is
   * also on this order. One physical fill on two open orders is a double dispense
   * and a double charge; the Order Detail banner is the only place a picker or
   * verifier working from a single order screen can see it.
   *
   * Returns `duplicates: []` for a closed order (the backend answers live-vs-live
   * only, so a routine refill of the same Rx never raises a banner).
   */
  async getDuplicateRxOrders(
    pharmacyId: string,
    orderId: string,
  ): Promise<{
    orderId: string;
    duplicates: Array<{
      rxNumbers: string[];
      order: { id: string; orderNumber: string; workflowStage: string; status: string };
    }>;
  }> {
    const response = await api.get(
      `${API_PREFIX}/orders/${pharmacyId}/${orderId}/duplicate-rx`,
    );
    return response.data?.data ?? response.data;
  },

  async resyncOrder(pharmacyId: string, orderId: string): Promise<Order> {
    const response = await api.post(`${API_PREFIX}/orders/${pharmacyId}/${orderId}/resync`);
    return response.data;
  },

  /**
   * Live prescription-status verdicts for an order. Re-reads the PMS — used by
   * the ready-to-ship and staging scans, which must not trust the resync stamp:
   * an order can sit at those stages for hours after its last resync, so a
   * prescription cancelled in between would be invisible in the stamp.
   *
   * Read-only, and it throws on failure rather than returning an empty verdict
   * list. The callers refuse the scan on a throw; an empty `gates` array means
   * the backend genuinely found no prescriptions to check.
   */
  async getOrderPmsGate(pharmacyId: string, orderId: string): Promise<OrderPmsGateResponse> {
    const response = await api.get(`${API_PREFIX}/orders/${pharmacyId}/${orderId}/pms-gate`);
    return response.data;
  },

  /**
   * Predict the new Rx numbers Kroll just generated for a list of basket
   * rxNumbers. Used by the "Confirm New Rx Numbers" step of ProcessBilling
   * to pre-fill an editable old→new mapping before the spawn commits.
   *
   * For each requested rxNumber the response includes:
   *   - predictedNewRxNumber (null when Kroll has no row for it)
   *   - isUnchanged (true when prediction equals input — staff hasn't
   *     refilled in Kroll yet, surface as a warning)
   *   - fillDate of the predicted Rx
   */
  async predictRxBumps(
    pharmacyId: string,
    billingItemId: string,
    rxNumbers: string[],
  ): Promise<{
    predictions: Array<{
      rxNumber: string;
      predictedNewRxNumber: string | null;
      medicationName: string | null;
      fillDate: string | null;
      isUnchanged: boolean;
    }>;
  }> {
    const csv = rxNumbers.map((r) => String(r).trim()).filter(Boolean).join(',');
    const response = await api.get(
      `${API_PREFIX}/v2/orders/${pharmacyId}/${billingItemId}/predict-rx-bumps`,
      { params: { rxNumbers: csv } },
    );
    return response.data;
  },

  /**
   * Add additional medications from the patient's persistent basket to an
   * in-flight order. Backend pins the listed rxNumbers to the order and
   * removes them from the queue. The order must be at BILLING or
   * CLINICAL_VERIFICATION stage; later stages reject with 400.
   */
  async addMedicationsToOrder(
    pharmacyId: string,
    orderId: string,
    rxNumbers: string[],
    options?: {
      prnRxNumbers?: string[];
      newMedications?: Array<{
        rxNumber: string;
        medicationName: string;
        din?: string;
        drugId?: string;
        strength?: string;
        quantity: number;
        daysSupply: number;
        isRefrigerated?: boolean;
        isPrn?: boolean;
      }>;
      /**
       * Old→new Rx bumps for subscription drugs whose chosen fill differs from
       * the basket's stored Rx. The backend rewrites the basket med's rxNumber
       * before claiming so the new fill is pinned without duplicating the entry.
       * The optional drug-identity fields re-identify the basket line on an
       * interchange swap (new fill has a different DIN, same Kroll chain).
       */
      rxBumps?: RxBumpInput[];
    },
  ): Promise<{ success: boolean; message: string; addedRxNumbers: string[]; orderId: string }> {
    const body: {
      rxNumbers?: string[];
      prnRxNumbers?: string[];
      newMedications?: NonNullable<typeof options>['newMedications'];
      rxBumps?: NonNullable<typeof options>['rxBumps'];
    } = {};
    if (rxNumbers.length > 0) body.rxNumbers = rxNumbers;
    if (options?.prnRxNumbers && options.prnRxNumbers.length > 0) {
      body.prnRxNumbers = options.prnRxNumbers;
    }
    if (options?.newMedications && options.newMedications.length > 0) {
      body.newMedications = options.newMedications;
    }
    if (options?.rxBumps && options.rxBumps.length > 0) {
      body.rxBumps = options.rxBumps;
    }
    const response = await api.post(
      `${API_PREFIX}/v2/orders/${pharmacyId}/${orderId}/add-medications`,
      body,
    );
    return response.data;
  },

  async bulkResyncStatuses(pharmacyId: string, orderIds: string[]): Promise<{ success: boolean; results: any[]; message: string }> {
    // skipQueueBump: callers apply the returned statuses to their rows in
    // place — a bus bump here would refetch the very queue they just painted.
    const response = await api.post(
      `${API_PREFIX}/orders/${pharmacyId}/bulk-resync-status`,
      { orderIds },
      { skipQueueBump: true },
    );
    return response.data;
  },

  /**
   * GP-1264: Return an order from the "Billed - To Be Cancelled" queue back into
   * billing Order Troubleshooting (used when an order was parked for cancellation
   * by mistake). Clears the to-be-cancelled flags and re-surfaces the order in
   * the "📦 Order Troubleshooting" tab.
   */
  async returnToOrderTroubleshooting(
    pharmacyId: string,
    orderId: string,
  ): Promise<{ success: boolean; message: string }> {
    const response = await api.post(
      `${API_PREFIX}/orders/${pharmacyId}/${orderId}/return-to-troubleshooting`,
    );
    return response.data;
  },

  /**
   * Live per-Rx cancellation status for one order (Scheduling Queue cancel gate).
   * Queries Kroll fresh, persists statuses, and reports whether every Rx is cancelled.
   */
  async getRxCancellationStatus(pharmacyId: string, orderId: string): Promise<RxCancellationStatus> {
    const response = await api.get(`${API_PREFIX}/orders/${pharmacyId}/${orderId}/rx-cancellation-status`);
    return response.data;
  },

  /**
   * GP-1148: Cancel a single Rx off a multi-Rx order via the rich cancellation
   * flow. Isolates the Rx (keeping the remaining Rx active on the order) and
   * then either hard-cancels it (`disposition: 'cancel'`) or parks it in the
   * "To Be Cancelled" review queue (`disposition: 'send_to_bill'`).
   */
  async cancelRxFromOrder(
    pharmacyId: string,
    orderId: string,
    dto: {
      rxNumber: string;
      disposition: 'cancel' | 'send_to_bill';
      cancellationReason: string;
      cancellationNotes?: string;
      newFollowUpDate?: string;
    },
  ): Promise<{ success: boolean; message: string; cancelledOrderId?: string; cancelledOrderNumber?: string }> {
    const response = await api.post(`${API_PREFIX}/orders/${pharmacyId}/${orderId}/cancel-rx`, dto);
    return response.data;
  },

  async getOrderFlow(
    pharmacyId: string,
    orderId: string,
  ): Promise<{
    success: boolean;
    data: {
      summary: any;
      events: Array<{
        timestamp: string;
        source: string;
        type: string;
        label: string;
        details?: any;
      }>;
    };
  }> {
    const response = await api.get(`${API_PREFIX}/orders/${pharmacyId}/${orderId}/flow`);
    return response.data;
  },

  async getWorkflowOverview(pharmacyId?: string): Promise<WorkflowOverview> {
    const id =
      pharmacyId ||
      (typeof window !== 'undefined' && localStorage.getItem('selectedPharmacyId')) ||
      '645b9c128158f06a385d5043';
    const response = await api.get(`${API_PREFIX}/v1/workflow/overview`, {
      params: { pharmacyId: id },
    });
    return response.data;
  },

  async getQueueOrders(
    queueType: string,
    params?: {
      followUpDate?: string;
      coordinator?: string;
      drugType?: string;
      focStatus?: string;
      page?: number;
      limit?: number;
    },
  ): Promise<QueueOrdersResponse> {
    const response = await api.get(`${API_PREFIX}/v1/workflow/queues/${queueType}`, {
      params,
    });
    return response.data;
  },

  async getOrderWorkflowStatus(orderId: string): Promise<OrderWorkflowDetail> {
    const response = await api.get(`${API_PREFIX}/v1/workflow/orders/${orderId}/status`);
    return response.data;
  },

  async continueOrder(
    orderId: string,
    data: {
      currentStep: number;
      action: string;
      verificationData?: any;
    },
  ): Promise<{
    orderId: string;
    success: boolean;
    newStep: number;
    newStepName: string;
    nextActions: string[];
    workflowProgress: number;
  }> {
    const response = await api.post(`${API_PREFIX}/v1/workflow/orders/${orderId}/continue`, data);
    return response.data;
  },

  async getIntegrationStatus(): Promise<IntegrationHealthStatus> {
    const response = await api.get(`${API_PREFIX}/v1/workflow/integration-status`);
    return response.data;
  },

  async verifyProduct(
    orderId: string,
    verificationData: {
      productBarcode: string;
      focBarcode?: string;
      lotNumber: string;
      expiryDate: string;
    },
    verifiedBy: string,
  ): Promise<any> {
    const response = await api.post(`${API_PREFIX}/v1/workflow/orders/${orderId}/verify-product`, {
      verificationData,
      verifiedBy,
    });
    return response.data;
  },

  async getWorkflowMetrics(params?: { date?: string; period?: string }): Promise<WorkflowMetrics> {
    const response = await api.get(`${API_PREFIX}/v1/workflow/metrics`, {
      params,
    });
    return response.data;
  },

  async assignOrder(
    orderId: string,
    data: {
      assignTo: string;
      role: string;
      notes?: string;
    },
  ): Promise<any> {
    const response = await api.post(`${API_PREFIX}/v1/workflow/orders/${orderId}/assign`, data);
    return response.data;
  },

  async escalateToTroubleshooting(
    orderId: string,
    data: {
      reason: string;
      priority: string;
      description: string;
      blockingStep: number;
    },
  ): Promise<any> {
    const response = await api.post(
      `${API_PREFIX}/v1/workflow/orders/${orderId}/troubleshoot`,
      data,
    );
    return response.data;
  },

  async getQueueFilters(queueType: string): Promise<any> {
    const response = await api.get(`${API_PREFIX}/v1/workflow/queues/${queueType}/filters`);
    return response.data;
  },

  // Descartes Integration Methods
  async createDescartesShipment(
    pharmacyId: string,
    orderId: string,
    data: {
      deliveryDate?: string;
      specialInstructions?: string;
      deliveryAddress?: {
        addressLine1: string;
        city: string;
        stateCode: string;
        postalCode: string;
      };
      patientName?: string;
      phoneNumber?: string;
      email?: string;
    },
  ): Promise<{
    success: boolean;
    shipmentId?: string;
    error?: string;
  }> {
    try {
      const response = await api.post(
        `${API_PREFIX}/orders/${pharmacyId}/${orderId}/descartes/shipment`,
        data,
      );
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || error.message,
      };
    }
  },

  async getDescartesShipmentStatus(
    pharmacyId: string,
    orderId: string,
  ): Promise<{
    hasShipment: boolean;
    shipmentId?: string;
    status?: string;
    createdAt?: string;
  }> {
    const response = await api.get(
      `${API_PREFIX}/orders/${pharmacyId}/${orderId}/descartes/status`,
    );
    return response.data;
  },

  async cancelDescartesShipment(
    pharmacyId: string,
    orderId: string,
    reason?: string,
  ): Promise<{
    success: boolean;
    error?: string;
  }> {
    try {
      const response = await api.post(
        `${API_PREFIX}/orders/${pharmacyId}/${orderId}/descartes/cancel`,
        { reason },
      );
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || error.message,
      };
    }
  },

  async rescheduleDescartesDelivery(
    pharmacyId: string,
    orderId: string,
    data: {
      newDeliveryDate: string;
      reason?: string;
    },
  ): Promise<{
    success: boolean;
    error?: string;
  }> {
    try {
      const response = await api.post(
        `${API_PREFIX}/orders/${pharmacyId}/${orderId}/descartes/reschedule`,
        data,
      );
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || error.message,
      };
    }
  },

  // Update order status for pharmacy workflow
  // Process billing for an order
  async processBilling(
    pharmacyId: string,
    orderId: string,
    data: {
      rxNumber: string;
      notes?: string;
      sendSmsNotification?: boolean;
    },
  ): Promise<any> {
    const response = await api.post(
      `${API_PREFIX}/orders/${pharmacyId}/${orderId}/process-billing`,
      data,
    );
    return response.data;
  },

  // Replace order items with new medications
  async replaceOrderItems(
    pharmacyId: string,
    orderId: string,
    items: Array<{
      medicationName: string;
      rxNumber?: string;
      quantity: number;
      daysSupply: number;
      sig?: string;
      isCompound?: boolean;
      isNarcotic?: boolean;
    }>,
  ): Promise<any> {
    const response = await api.put(`${API_PREFIX}/orders/${pharmacyId}/${orderId}/items`, {
      items,
    });
    return response.data;
  },

  // Convert order to clinical verification
  async convertToClinical(
    pharmacyId: string,
    orderId: string,
    data?: {
      medications?: any[];
      notes?: string;
    },
  ): Promise<{
    success: boolean;
    orderId: string;
    status: string;
    workflowStage: string;
    message: string;
  }> {
    const response = await api.post(
      `${API_PREFIX}/orders/${pharmacyId}/${orderId}/convert-to-clinical`,
      data || {},
    );
    return response.data;
  },

  // Get clinical verification queue
  async getClinicalVerificationQueue(
    pharmacyId?: string,
    params?: {
      page?: number;
      limit?: number;
    },
  ): Promise<{
    items: any[];
    total: number;
    page: number;
    pageSize: number;
  }> {
    const queryParams = new URLSearchParams();
    if (pharmacyId) queryParams.append('pharmacyId', pharmacyId);
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());

    const response = await api.get(
      `${API_PREFIX}/orders/clinical-verification-queue?${queryParams.toString()}`,
    );
    return response.data;
  },

  async updateOrderStatus(
    pharmacyId: string,
    orderId: string,
    action: 'clinical-verify' | 'package' | 'product-verify',
    data: {
      verifiedBy?: string;
      packagedBy?: string;
      notes?: string;
      trackingNumber?: string;
      barcode?: string;
    },
  ): Promise<{ success: boolean; message?: string; error?: string }> {
    try {
      const response = await api.patch(
        `${API_PREFIX}/orders/${pharmacyId}/${orderId}/${action}`,
        data,
      );
      return response.data;
    } catch (error: any) {
      console.error(`Failed to ${action} order:`, error);
      return {
        success: false,
        error: error.response?.data?.message || error.message,
      };
    }
  },

  // Simulate delivery for demo purposes
  async simulateDelivery(
    pharmacyId: string,
    driverName: string,
    preview: boolean = false,
  ): Promise<{
    success: boolean;
    message: string;
    deliveredOrders: any[];
    missedOrders: any[];
    smsMessages: Array<{
      orderId: string;
      orderNumber: string;
      phone: string;
      message: string;
      rescheduledDate: string;
    }>;
    deliveredCount: number;
    missedCount: number;
  }> {
    try {
      const queryParam = preview ? '?preview=true' : '';
      const response = await api.post(
        `${API_PREFIX}/orders/${pharmacyId}/simulate-delivery/${encodeURIComponent(driverName)}${queryParam}`,
      );
      return response.data;
    } catch (error: any) {
      console.error('Failed to simulate delivery:', error);
      throw error;
    }
  },

  // Apply user-selected delivery results (DEMO MODE)
  async applyDeliveryResults(
    pharmacyId: string,
    driverName: string,
    deliveredOrderIds: string[],
    missedOrderIds: string[],
  ): Promise<{
    success: boolean;
    message: string;
    deliveredOrders: any[];
    missedOrders: any[];
    smsMessages: Array<{
      orderId: string;
      orderNumber: string;
      phone: string;
      message: string;
      rescheduledDate: string;
    }>;
    deliveredCount: number;
    missedCount: number;
  }> {
    try {
      const response = await api.post(
        `${API_PREFIX}/orders/${pharmacyId}/apply-delivery-results/${encodeURIComponent(driverName)}`,
        { deliveredOrderIds, missedOrderIds },
      );
      return response.data;
    } catch (error: any) {
      console.error('Failed to apply delivery results:', error);
      throw error;
    }
  },

  // Complete individual delivery (delivered or missed)
  async completeDelivery(
    pharmacyId: string,
    orderId: string,
    outcome: 'delivered' | 'missed',
    userId: string,
    missedReason?: string,
    rescheduleDate?: string,
    deliveryDate?: string, // Optional: User-provided delivery date for manual completion
    ltomDeliveryVerified?: boolean, // GP-1085: staff verified the LToM–MM delivery with the Mississauga team
  ): Promise<{
    success: boolean;
    message: string;
    order: {
      id: string;
      orderNumber: string;
      workflowStage: string;
      deliveredAt?: string;
      deliveryDate?: string;
      troubleshootingReason?: string;
    };
    billingItemUpdated?: boolean;
    nextFollowUpDate?: string | null;
    smsSent?: boolean;
    rescheduledDate?: string;
  }> {
    try {
      const response = await api.post(
        `${API_PREFIX}/orders/${pharmacyId}/${orderId}/complete-delivery`,
        {
          outcome,
          userId,
          missedReason,
          rescheduleDate,
          deliveryDate,
          ltomDeliveryVerified,
        },
      );
      return response.data;
    } catch (error: any) {
      console.error('Failed to complete delivery:', error);
      throw error;
    }
  },

  // Bulk complete deliveries (mark multiple orders as delivered)
  async bulkCompleteDelivery(
    pharmacyId: string,
    orderIds: string[],
    ltomDeliveryVerified?: boolean, // GP-1085: staff verified the LToM–MM deliveries with the Mississauga team
  ): Promise<{
    success: boolean;
    message: string;
    results: Array<{
      orderId: string;
      orderNumber?: string;
      success: boolean;
      error?: string;
      billingItemUpdated?: boolean;
      nextFollowUpDate?: string;
    }>;
    summary: {
      successCount: number;
      failureCount: number;
      billingItemsUpdated: number;
    };
  }> {
    try {
      const response = await api.post(
        `${API_PREFIX}/orders/${pharmacyId}/bulk-complete-delivery`,
        { orderIds, ltomDeliveryVerified },
      );
      return response.data;
    } catch (error: any) {
      console.error('Failed to bulk complete deliveries:', error);
      throw error;
    }
  },

  // Sign out bag as pending — move orders to out_for_delivery (awaiting ShipStation confirmation)
  async bulkSignOutPending(
    pharmacyId: string,
    orderIds: string[],
  ): Promise<{
    success: boolean;
    message: string;
    results: Array<{ orderId: string; orderNumber?: string; success: boolean; error?: string }>;
    summary: { successCount: number; failureCount: number };
  }> {
    try {
      const response = await api.post(
        `${API_PREFIX}/orders/${pharmacyId}/bulk-sign-out-pending`,
        { orderIds },
      );
      return response.data;
    } catch (error: any) {
      console.error('Failed to bulk sign out pending:', error);
      throw error;
    }
  },

  /**
   * PRX-16 — scan a delivery label into its courier's open bag. 404s at any
   * pharmacy that has not turned one-page shipping on.
   */
  async scanIntoCourierBag(
    pharmacyId: string,
    orderId: string,
  ): Promise<{
    success: boolean;
    orderId: string;
    orderNumber: string;
    courierId: string;
    courierName: string;
    bagNumber: string;
    alreadyInBag: boolean;
    message: string;
  }> {
    const response = await api.post(`${API_PREFIX}/orders/${pharmacyId}/shipping/scan`, {
      orderId,
    });
    return response.data;
  },

  /**
   * PRX-16 — close the courier's bag and sign it out, in one call and one
   * transaction. Nothing is marked delivered and no next bag is opened.
   *
   * `summary.successCount` is what SHIPPED and `skippedCount` is what a courier
   * had already taken, counted apart so the page never claims a hand-over it did
   * not make. Those two and nothing else: the route cannot produce a failure
   * count — a gate refusal throws a 409 with a sentence and no body, and a skip
   * is a `success: true` row with an `error` string.
   */
  async handOverToCourier(
    pharmacyId: string,
    courierId: string,
    bagNumber: string,
  ): Promise<{
    success: boolean;
    message: string;
    bagNumber: string;
    courierName: string;
    results: Array<{ orderId: string; orderNumber?: string; success: boolean; error?: string }>;
    summary: { successCount: number; skippedCount: number };
  }> {
    const response = await api.post(
      `${API_PREFIX}/orders/${pharmacyId}/couriers/${courierId}/hand-over`,
      { bagNumber },
    );
    return response.data;
  },

  /**
   * PRX-165 WP-26 (ADR-135) — Make shipment for one courier's group on Ready to
   * ship → Unassigned. Each order is booked (through the courier's client when
   * it is connected, otherwise recorded as booked with the courier directly),
   * put in the courier's open bag, and reported on its own row.
   */
  async makeShipment(
    pharmacyId: string,
    courierId: string,
    body: {
      orderIds: string[];
      courierReference?: string;
      confirmManualBooking?: boolean;
      /** PRX-21 WP-31 (review N3): the orders whose earlier courier-API booking staff cancelled with the courier. */
      confirmEarlierBookingCancelledOrderIds?: string[];
    },
  ): Promise<MakeShipmentResponse> {
    const response = await api.post(
      `${API_PREFIX}/orders/${pharmacyId}/couriers/${courierId}/make-shipment`,
      body,
    );
    return response.data;
  },

  /**
   * PRX-21 WP-33a — the Canada Post label PDF for an order PHOX booked with
   * Canada Post. A refusal comes back as a JSON blob; it is read back into the
   * error so the page can say the server's sentence.
   */
  async getCourierLabel(pharmacyId: string, orderId: string): Promise<Blob> {
    try {
      const response = await api.get(`${API_PREFIX}/orders/${pharmacyId}/${orderId}/courier-label`, {
        responseType: 'blob',
      });
      return response.data;
    } catch (error: any) {
      const data = error?.response?.data;
      if (data instanceof Blob) {
        try {
          error.response.data = JSON.parse(await data.text());
        } catch {
          // Not JSON: the page says its own fallback.
        }
      }
      throw error;
    }
  },

  /**
   * Get order timeline with all events
   */
  async getOrderTimeline(
    pharmacyId: string,
    orderId: string,
    options?: { includeSystemEvents?: boolean; eventTypes?: string[] },
  ): Promise<any> {
    try {
      const params = new URLSearchParams();
      if (options?.includeSystemEvents !== undefined) {
        params.append('includeSystemEvents', String(options.includeSystemEvents));
      }
      if (options?.eventTypes?.length) {
        params.append('eventTypes', options.eventTypes.join(','));
      }

      const queryString = params.toString();
      const url = `${API_PREFIX}/orders/${pharmacyId}/${orderId}/timeline${queryString ? `?${queryString}` : ''}`;

      const response = await api.get(url);
      return response.data.data; // Extract data from success response
    } catch (error: any) {
      console.error('Failed to get order timeline:', error);
      throw error;
    }
  },

  // ==================== PER-STAGE ASSIGNMENT ====================

  /**
   * Assign an order to a staff member for a specific workflow stage
   */
  async assignOrderToStage(
    pharmacyId: string,
    orderId: string,
    stage: 'clinical' | 'product' | 'packaging' | 'staging',
    assignedTo: string | null,
  ): Promise<{ success: boolean; message: string; data: any }> {
    try {
      const response = await api.post(
        `${API_PREFIX}/orders/${pharmacyId}/${orderId}/assign`,
        { stage, assignedTo },
      );
      return response.data;
    } catch (error: any) {
      console.error('Failed to assign order to stage:', error);
      throw error;
    }
  },

  /**
   * Bulk assign orders to a staff member for a specific workflow stage
   */
  async bulkAssignOrdersToStage(
    pharmacyId: string,
    orderIds: string[],
    stage: 'clinical' | 'product' | 'packaging' | 'staging',
    assignedTo: string | null,
  ): Promise<{ success: boolean; message: string; updatedCount: number }> {
    try {
      const response = await api.post(
        `${API_PREFIX}/orders/${pharmacyId}/bulk-assign-stage`,
        { orderIds, stage, assignedTo },
      );
      return response.data;
    } catch (error: any) {
      console.error('Failed to bulk assign orders to stage:', error);
      throw error;
    }
  },

  // ==================== ADMIN: STAGE OVERRIDE ====================

  /**
   * Get order by RX number (used for stage override search)
   */
  async getOrderByRxNumber(
    pharmacyId: string,
    rxNumber: string,
  ): Promise<any> {
    const response = await api.get(
      `${API_PREFIX}/orders/${pharmacyId}/by-rx/${encodeURIComponent(rxNumber)}`,
    );
    return response.data;
  },

  /**
   * Get ALL orders matching an RX number (admin stage-override list picker).
   * Returns [] when no matches; does not throw.
   */
  async getOrdersByRxNumber(
    pharmacyId: string,
    rxNumber: string,
  ): Promise<any[]> {
    const response = await api.get(
      `${API_PREFIX}/orders/${pharmacyId}/by-rx-list/${encodeURIComponent(rxNumber)}`,
    );
    return response.data;
  },

  /**
   * ADMIN ONLY: Force move an order to any workflow stage
   * Bypasses normal validation, logs action for audit
   */
  async forceStageOverride(
    pharmacyId: string,
    orderId: string,
    dto: {
      targetStage: string;
      reason: string;
      syncToDescartes?: boolean;
      notes?: string;
    },
  ): Promise<{
    success: boolean;
    order: {
      id: string;
      orderNumber: string;
      previousStage: string;
      newStage: string;
      workflowStage: string;
    };
    message: string;
    warnings?: string[];
    descartesSync?: {
      attempted: boolean;
      success: boolean;
      error?: string;
    };
  }> {
    const response = await api.post(
      `${API_PREFIX}/orders/${pharmacyId}/${orderId}/force-stage-override`,
      dto,
    );
    return response.data;
  },

  /**
   * Update a specific note field on an order
   * @param pharmacyId - The pharmacy ID
   * @param orderId - The order ID
   * @param noteType - The type of note to update
   * @param value - The new note value
   */
  async updateOrderNote(
    pharmacyId: string,
    orderId: string,
    noteType: 'internalNote' | 'clinicalNote' | 'deliveryOrderNote',
    value: string,
  ): Promise<{ success: boolean; message?: string }> {
    try {
      // Map noteType to the correct endpoint and body field
      const configMap: Record<string, { endpoint: string; bodyField: string }> = {
        internalNote: { endpoint: 'internal-note', bodyField: 'internalNote' },
        clinicalNote: { endpoint: 'clinical-note', bodyField: 'clinicalNote' },
        deliveryOrderNote: { endpoint: 'delivery-instructions', bodyField: 'deliveryInstructions' },
      };
      const config = configMap[noteType] || { endpoint: 'note', bodyField: 'note' };

      const response = await api.put(
        `${API_PREFIX}/orders/${orderId}/${config.endpoint}`,
        { [config.bodyField]: value },
      );
      return { success: true, message: response.data?.message || 'Note updated' };
    } catch (error: any) {
      console.error('Failed to update order note:', error);
      throw error;
    }
  },

  /**
   * GP-1380: preview the clinical-equivalence check for an RX swap.
   *
   * Read-only. Returns the same six-criteria comparison the swap endpoint
   * enforces, so the dialog's table can never disagree with the server's gate.
   * Throws (400) when Kroll has no row for either RX — an unverifiable swap is
   * blocked, never quietly allowed.
   */
  async checkRxSwapEquivalence(
    pharmacyId: string,
    orderId: string,
    oldRxNumber: string,
    newRxNumber: string,
  ): Promise<RxSwapEquivalence> {
    const response = await api.get(
      `${API_PREFIX}/orders/${pharmacyId}/${orderId}/rx-swap-check`,
      { params: { oldRxNumber, newRxNumber } },
    );
    return response.data;
  },

  /**
   * Swap RX number on a completed/delivered order
   * Used when patient gets retroactive insurance and RX is rebilled in Kroll
   *
   * GP-1380: the server re-runs the six-criteria equivalence check and rejects a
   * mismatched swap unless `pharmacistOverride` is sent by a pharmacist with a
   * reason.
   *
   * @param pharmacyId - Pharmacy ID
   * @param orderId - Order ID
   * @param data - Swap request data
   * @returns Success response with old/new RX numbers and the GP-1433 repricing
   *          outcome (the copay re-pulled from Kroll for the new RX). `applied:
   *          false` means Kroll could not be reached or had no receipt — the
   *          order kept its pre-swap pricing and staff must check it by hand.
   */
  async swapRxNumber(
    pharmacyId: string,
    orderId: string,
    data: {
      newRxNumber: string;
      oldRxNumber?: string;
      reason?: string;
      pharmacistOverride?: boolean;
    },
  ): Promise<{
    success: boolean;
    message: string;
    previousRxNumber: string;
    newRxNumber: string;
    repricing?: {
      applied: boolean;
      /** How the new copay was derived when applied (mirrors the backend's SwapRxRepricingMethod). */
      method?: 'sum_of_medications' | 'swapped_medication_delta';
      previousCopayAmount: number;
      newCopayAmount: number;
      alreadyPaid: number;
      outstandingBalance: number;
      overpaidBy: number;
      /** Typed reason when NOT applied (mirrors the backend's SwapRxRepricingReasonCode). */
      reasonCode?:
        | 'KROLL_SERVICE_UNAVAILABLE'
        | 'KROLL_LOOKUP_FAILED'
        | 'KROLL_NO_RECEIPT'
        | 'KROLL_PRICE_MISSING'
        | 'NO_MATCHING_MEDICATION'
        | 'CC_CHILD_CONSOLIDATED'
        | 'CC_PARENT_OLD_PRICE_UNKNOWN';
      reason?: string;
    };
    equivalence?: RxSwapEquivalence;
  }> {
    try {
      const response = await api.put(
        `${API_PREFIX}/orders/${pharmacyId}/${orderId}/swap-rx-number`,
        data,
      );
      return response.data;
    } catch (error: any) {
      console.error('Failed to swap RX number:', error);
      throw error;
    }
  },

  /**
   * GP-1030: reprocess / resend an order after a physical mishap (courier
   * damage, patient refusal, lost parcel). Creates an `RP-` replacement order at
   * Packaging that re-runs Packaging → Product Verification → Ready to Ship →
   * Descartes against the SAME Kroll fill — no Rx cancellation, no rebill, and
   * the patient is not charged again.
   */
  async reprocessOrder(
    pharmacyId: string,
    orderId: string,
    data: {
      reason: string;
      notes?: string;
      /** Plain YYYY-MM-DD — never `.toISOString()` (see Date Handling Rules). */
      deliveryDate: string;
    },
  ): Promise<{
    success: boolean;
    sourceOrderId: string;
    sourceOrderNumber: string;
    replacementOrderId: string;
    replacementOrderNumber: string;
    workflowStage: string;
    deliveryDate: string;
    reason: string;
  }> {
    const response = await api.post(
      `${API_PREFIX}/orders/${pharmacyId}/${orderId}/reprocess`,
      data,
    );
    return response.data;
  },

  // Archive the SMS failure for an order (reversible)
  async archiveOrderSmsFailure(pharmacyId: string, orderId: string): Promise<void> {
    try {
      await api.put(`${API_PREFIX}/orders/${pharmacyId}/${orderId}/archive-sms-failure`);
    } catch (error: any) {
      console.error('Failed to archive order SMS failure:', error);
      throw error;
    }
  },

  // Unarchive the SMS failure for an order (reverse of archive)
  async unarchiveOrderSmsFailure(pharmacyId: string, orderId: string): Promise<void> {
    try {
      await api.put(`${API_PREFIX}/orders/${pharmacyId}/${orderId}/unarchive-sms-failure`);
    } catch (error: any) {
      console.error('Failed to unarchive order SMS failure:', error);
      throw error;
    }
  },

  // Alert Status Queue — lists stuck/overdue delivery orders
  async getAlertStatusQueue(params: {
    pharmacyId: string;
    agingBucket?: string;
    deliveryType?: string;
    page?: number;
    limit?: number;
    sortBy?: string;
    sortOrder?: string;
  }): Promise<{
    success: boolean;
    data: AlertStatusItem[];
    metadata: { total: number; page: number; pageSize: number; totalPages: number };
    stats: AgingBucketStats;
  }> {
    const searchParams = new URLSearchParams();
    searchParams.append('pharmacyId', params.pharmacyId);
    // Send agingBucket even when 'all' — the backend defaults a MISSING bucket to
    // 'overdue', so omitting 'all' would silently show only overdue rows.
    if (params.agingBucket) searchParams.append('agingBucket', params.agingBucket);
    if (params.deliveryType && params.deliveryType !== 'all') searchParams.append('deliveryType', params.deliveryType);
    if (params.page !== undefined) searchParams.append('page', String(params.page));
    if (params.limit !== undefined) searchParams.append('limit', String(params.limit));
    if (params.sortBy) searchParams.append('sortBy', params.sortBy);
    if (params.sortOrder) searchParams.append('sortOrder', params.sortOrder);
    const response = await api.get(`${API_PREFIX}/orders/alert-status-queue?${searchParams.toString()}`);
    return response.data;
  },

  // Delivery Remediation — close stuck out-for-delivery orders
  async closeStuckDeliveries(params: {
    deliveryTypeScope?: 'all' | 'courier' | 'inhouse';
    dryRun?: boolean;
    orderIds?: string[];
    limit?: number;
    afterId?: string;
    confirmToken?: string;
  }): Promise<{
    success: boolean;
    dryRun: boolean;
    summary: { closed: number; manual: number; skipped: number; total: number };
    results: Array<{
      orderNumber: string;
      decision: 'closed' | 'manual' | 'skipped';
      deliveryDate?: string;
      dateSource?: string;
      reason?: string;
    }>;
    batch?: {
      processed: number;
      limit: number;
      lastId: string | null;
      hasMore: boolean;
      total: number;
      stoppedEarly: boolean;
    };
    confirmToken?: string;
  }> {
    const response = await api.post(
      `${API_PREFIX}/orders/remediation/close-stuck-deliveries`,
      params,
    );
    return response.data;
  },

  // GP-984: recompute the denormalized order drug summary (din/qty + metadata
  // mirrors) for orders that drifted after a troubleshooting med-set change.
  async repairDrugSummary(params: {
    statusScope?: 'active' | 'all';
    dryRun?: boolean;
    orderIds?: string[];
    limit?: number;
    afterId?: string;
    confirmToken?: string;
  }): Promise<{
    success: boolean;
    dryRun: boolean;
    summary: { repaired: number; clean: number; skipped: number; total: number };
    results: Array<{
      orderNumber: string;
      decision: 'repaired' | 'clean' | 'skipped';
      before?: { din: string | null; quantity: number | null; metaDin: string | null };
      after?: { din: string | null; quantity: number | null; metaDin: string | null };
      reason?: string;
    }>;
    batch?: {
      processed: number;
      limit: number;
      lastId: string | null;
      hasMore: boolean;
      total: number;
      stoppedEarly: boolean;
    };
    confirmToken?: string;
  }> {
    const response = await api.post(
      `${API_PREFIX}/orders/remediation/repair-drug-summary`,
      params,
    );
    return response.data;
  },
};

// Export as default for backward compatibility
export default orderService;
